import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RefreshCw, Inbox } from 'lucide-react'
import { useState } from 'react'
import { useEventHeaders, useEvents, useEventsStats, useInboxes } from '@/api'
import { EventStatus } from '@/lib/enum-display'
import { EventListItem } from '@/components/classification/event-list-item'

const STATUS_FILTERS = [
  { value: 'all', label: 'All emails' },
  { value: EventStatus.DONE, label: 'Finished' },
  { value: EventStatus.ERROR, label: 'Problems' },
  { value: EventStatus.PENDING, label: 'Waiting' },
  { value: EventStatus.ANALYZING, label: 'In progress' },
]

export function EventsPage({ inboxId }: { inboxId?: string }) {
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [inboxFilter, setInboxFilter] = useState<string>(inboxId ?? 'all')
  const [page, setPage] = useState(1)
  const limit = 20

  const { data: inboxes } = useInboxes()
  const { data: stats, isLoading: statsLoading } = useEventsStats({
    emailAccountId: inboxFilter !== 'all' ? inboxFilter : undefined,
  })
  const { data: events, isLoading, refetch, isFetching } = useEvents({
    emailAccountId: inboxFilter !== 'all' ? inboxFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    limit,
    offset: (page - 1) * limit,
  })

  const headers = useEventHeaders(events?.events.map((event) => event.uuid) ?? [])
  const totalPages = events ? Math.ceil(events.total / limit) : 1
  const counts = stats
    ? [
        { label: 'Emails', value: stats.total },
        { label: 'Finished', value: stats.done },
        { label: 'In progress', value: stats.pending + stats.analyzing },
        { label: 'Problems', value: stats.error, alert: stats.error > 0 },
      ]
    : []

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight lg:text-3xl">Events</h1>
          <p className="text-muted-foreground">What happened to each email.</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
          className="gap-2"
        >
          <RefreshCw className={`size-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {statsLoading ? (
        <Skeleton className="h-20 w-full rounded-xl" />
      ) : (
        stats && (
          <Card className="py-0">
            <dl className="grid grid-cols-2 sm:grid-cols-4">
              {counts.map((count) => (
                <div key={count.label} className="px-4 py-3 sm:px-6 sm:py-4">
                  <dt className="text-xs text-muted-foreground">{count.label}</dt>
                  <dd
                    className={`text-2xl font-semibold tabular-nums ${count.alert ? 'text-destructive' : ''}`}
                  >
                    {count.value.toLocaleString()}
                  </dd>
                </div>
              ))}
            </dl>
          </Card>
        )
      )}

      <Card>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[150px]" aria-label="Processing status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTERS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {inboxes && (inboxes.length > 1 || inboxFilter !== 'all') && (
              <Select
                value={inboxFilter}
                onValueChange={(v) => {
                  setInboxFilter(v)
                  setPage(1)
                }}
              >
                <SelectTrigger className="w-[200px]" aria-label="Inbox">
                  <SelectValue placeholder="Inbox" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All inboxes</SelectItem>
                  {inboxes.map((inbox) => (
                    <SelectItem key={inbox.uuid} value={inbox.uuid}>
                      {inbox.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : events && events.events.length > 0 ? (
            <>
              <div className="divide-y border-y">
                {events.events.map((event) => (
                  <EventListItem
                    key={event.uuid}
                    event={event}
                    headers={headers.data ? (headers.data[event.uuid] ?? null) : headers.isError ? null : undefined}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                  <p className="text-sm text-muted-foreground">
                    {((page - 1) * limit + 1).toLocaleString()}–
                    {Math.min(page * limit, events.total).toLocaleString()} of{' '}
                    {events.total.toLocaleString()}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Inbox className="mb-3 size-8 text-muted-foreground" />
              <h2 className="text-lg font-semibold">No emails yet</h2>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                Emails show up here once an inbox is processing.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
